import { DataCardComponent } from 'components/DataCardComponent'
import { DataCard } from 'components/DataCardComponent/styles'
import EmptyDataCard from 'components/DataCardComponent/EmptyDataCard'
import { timeInSeconds } from 'helper/timeFormatter'
import { useEffect, useMemo, useState } from 'react'
import { AiOutlinePlusSquare } from 'react-icons/ai'
import { HiOutlineOfficeBuilding } from 'react-icons/hi'
import { useSelector } from 'react-redux'
import { RootReducerState } from 'types/global'
import { LocationReducer } from 'types/location'
import { NotificationReducer } from 'types/notification'
import { SectorReducer } from 'types/sector'
import { HrCustomLine, ReportDataContainer, ReportDataGeneral, ReportDataMainContainer, ReportDataNew } from '../../styles'
import BarGraph from '../Graphs/BarGraph'
import BirdGraph, { formatDataBirdGraph } from '../Graphs/BirdGraph'
import HalfPieGraph from '../Graphs/HalfPieGraph'
import { OMSOption, getTotalPerIncidentSector, getTotalPerUnityAndSector, monthTotalOption as perTotalMonthOption, reclassifiedIncidentTypeOption } from '../Graphs/getOptions'
import { ArchivedAndNewContainer } from './ArchivedAndNewContainer'
import { FinalizedContainer } from './FinalizedContainer'
import { MassNotificationContainer } from './MassNotificationContainer'
import CardRendrer from './CardRendrer'
import MultipleInfoGraph from '../Graphs/MultipleInfoGraph'

interface ReportDataProps {
  setTooltipAlert?: React.Dispatch<React.SetStateAction<boolean>>,
  dashboards?: string[]
}
const ReportData: React.FC<ReportDataProps> = ({
  setTooltipAlert,
  dashboards
}) => {
  useEffect(() => {
    setTooltipAlert(false)
  }, [])

  const [timeStat, setTimeStat] = useState('')

  const notificationSelector: NotificationReducer = useSelector(
    (state: RootReducerState) => state.notificationsReducer
  )
  const sectorSelector: SectorReducer = useSelector(
    (state: RootReducerState) => state.sectorReducers
  )
  const locationSelector: LocationReducer = useSelector((state: RootReducerState) => state.locationReducers)

  useEffect(() => {
    const stats = notificationSelector.sumOfTimeEventToNotification
    const time = timeInSeconds(stats?.totalSeconds)
    setTimeStat(time)
  }, [notificationSelector.sumOfTimeEventToNotification])  
  

  const newNotificationCardStyle = 'h-[120px] min-w-[170px]'
  const generalNotificationCardStyle = 'w-[195px]  h-[100px]'
  const generalNotificationLoaderStyle = 'bg-[#EBEBEB] w-[195px]  h-[100px] border  border-solid border-[#E2E5EC] p-4'

  const getOpenNotification = () => {
    if (!notificationSelector.notificationCountByStatus?.['total']?.count) {
      return `0`
    }
    const nova = notificationSelector.notificationCountByStatus?.['Nova']?.count
    const aguardando = notificationSelector.notificationCountByStatus?.['Aguardando']?.count
    const tratando = notificationSelector.notificationCountByStatus?.['Em Tratamento']?.count
    const pendente = notificationSelector.notificationCountByStatus?.['Pendente Finalização']?.count
    return parseInt(nova) + parseInt(aguardando) + parseInt(tratando) + parseInt(pendente) + ""
  }

  const generalData = useMemo(() => {  
    return [
      {
        loading: locationSelector.locationCountLoading,
        loaderStyle: generalNotificationLoaderStyle,
        mainComponent: <DataCardComponent
          mainContainerStyle={generalNotificationCardStyle}
          units={"Unidades"}
          number={locationSelector.locationWithNotificationCount + ""}
          icon={<HiOutlineOfficeBuilding size={25}
            color='#F0B323'
          />} />
      },
      {
        loading: sectorSelector.loadingReportCount,
        loaderStyle: generalNotificationLoaderStyle,
        mainComponent: <DataCardComponent
          mainContainerStyle={generalNotificationCardStyle}
          units={"Setores"}
          number={`${sectorSelector?.sectorWithNotificationCount || 0}`}
          icon={<AiOutlinePlusSquare size={25} color='#F0B323' />} />
      },
      {
        loading: notificationSelector.loading,
        loaderStyle: 'w-[493px] h-[100px] bg-[#EBEBEB]',
        mainComponent: <ArchivedAndNewContainer
          totalNumber={notificationSelector.notificationCountByStatus?.['total']?.count}
          archivedNumber={notificationSelector.notificationCountByStatus?.['Arquivada']?.count}
        />

      },
    ]
  }, [locationSelector, sectorSelector, notificationSelector])

  const newAndAcceptedSection = useMemo(() => {
    const loaderStyle = `${newNotificationCardStyle} col-span-1 bg-[#EBEBEB]`
    return [
      {
        loading: notificationSelector.loading,
        loaderStyle: loaderStyle,
        mainComponent: <DataCardComponent
          subText={'Status'}
          mainContainerStyle={`${newNotificationCardStyle} col-span-1`}
          units={"Novas"}
          number={notificationSelector.notificationCountByStatus?.['Nova']?.count} />
      },
      {
        loading: notificationSelector.loading,
        loaderStyle: loaderStyle,
        mainComponent: <DataCardComponent
          subText={'Status'}
          mainContainerStyle={`${newNotificationCardStyle} col-span-1`}
          units={"Aguardando"}
          number={notificationSelector.notificationCountByStatus?.['Aguardando']?.count} />
      },
      {
        loading: notificationSelector.loading,
        loaderStyle: loaderStyle,
        mainComponent: <DataCardComponent
          subText={'Status'}
          mainContainerStyle={`${newNotificationCardStyle} col-span-1`}
          units={"Em tratamento"}
          number={notificationSelector.notificationCountByStatus?.['Em Tratamento']?.count} />
      },
      {
        loading: notificationSelector.loading,
        loaderStyle: loaderStyle,
        mainComponent: <DataCardComponent
          subText={'Status'}
          mainContainerStyle={`${newNotificationCardStyle} col-span-1`}
          units={"Pendente finaliz."}
          number={notificationSelector.notificationCountByStatus?.['Pendente Finalização']?.count} />
      },
      {
        loading: notificationSelector.loading,
        loaderStyle: loaderStyle,
        mainComponent: <DataCardComponent
          mainContainerStyle={`${newNotificationCardStyle} col-span-1`}
          units={"Notificações em aberto"}
          number={getOpenNotification()} />
      }
    ]
  }, [notificationSelector])

  const autoNotificationValue = useMemo(() => {
    const value = notificationSelector?.autoNotification
    if (value === null || value === undefined || isNaN(value)) {
      return '0%'
    }

    return `${Number(value.toFixed(1))}%`
  }, [notificationSelector?.autoNotification])

  const graveObitoStats = useMemo(() => {
    console.log("Calculate subdamage wher death or severe damage level")
    const data = notificationSelector?.notificationCountBySubDamageLevel ?? []
    const total = data.reduce((acc, item) => acc + Number(item.count || 0), 0)

    const labelEvitavel = 'Evento Adverso - Evitável'
    const labelInevitavel = 'Reação Adversa - Inevitável'

    let countEvitavel = 0
    let countInevitavel = 0
    data.forEach((item) => {
      const v = (item.value || '').toLowerCase().trim()
      const count = Number(item.count) || 0
      if (v.includes('evitável') || v.includes('evitavel') || v.includes('evento') || v.includes('u_evento_adverso')) {
        countEvitavel += count
      } else if (v.includes('inevitável') || v.includes('inevitavel') || v.includes('reação') || v.includes('reacao') || v.includes('u_reacao')) {
        countInevitavel += count
      }
    })
    
    const items = [
      {
        label: labelEvitavel,
        count: countEvitavel,
        percent: total > 0
          ? Number(((countEvitavel / total) * 100).toFixed(1))
          : 0
      },
      {
        label: labelInevitavel,
        count: countInevitavel,
        percent: total > 0
          ? Number(((countInevitavel / total) * 100).toFixed(1))
          : 0
      }
    ]

    return { total, items }
  }, [notificationSelector?.notificationCountBySubDamageLevel])

  return (
    <ReportDataMainContainer data-testid="test-relatorio-data" className='overflow-x-auto' >
      <ReportDataContainer >
        <ReportDataGeneral>
          <div className='mb-2'>
            <span className='text-darkGray text-sm font-bold'>Das notificações gerais:</span>
          </div>
          <div className='flex gap-4'>
            {
              generalData?.map((item, idx) => {
                return <CardRendrer
                  key={idx}
                  loading={item.loading}
                  loaderStyle={item.loaderStyle}
                  mainComponent={
                    item.mainComponent
                  }
                />
              })
            }
          </div>
        </ReportDataGeneral>
      </ReportDataContainer>
      
      <HrCustomLine />
      
      <ReportDataContainer>
        <ReportDataNew>
          <div className='mb-2'>
            <span className='text-darkGray text-sm font-bold'>Das notificações Novas e Aceitas:</span>
          </div>
          <div className='grid grid-cols-5 gap-4 max-w-[915px] pb-4'>
            {
              newAndAcceptedSection?.map((item, idx) => {
                return (
                  <CardRendrer
                    key={idx}
                    loading={item.loading}
                    loaderStyle={item.loaderStyle}
                    mainComponent={
                      item.mainComponent
                    }
                  />
                )
              })
            }

            <div className='col-span-5 grid grid-cols-[290px_16px_170px_16px_423px] gap-0 max-w-[915px] items-stretch'>
              <CardRendrer
                loading={notificationSelector.loading}
                loaderStyle='w-full h-[120px] bg-[#EBEBEB] border border-solid border-[#E2E5EC] p-4'
                mainComponent={
                  <FinalizedContainer
                    totalNumber={notificationSelector.notificationCountByStatus?.['Finalizada']?.count || '0'}
                    withTreatmentNumber={notificationSelector.finalizedBySubStatus?.withTreatment?.toString() || '0'}
                    withoutTreatmentNumber={notificationSelector.finalizedBySubStatus?.withoutTreatment?.toString() || '0'}
                  />
                }
              />
              <div />
              <CardRendrer
                loading={notificationSelector.loading}
                loaderStyle='w-full h-[120px] bg-[#EBEBEB] border border-solid border-[#E2E5EC] p-4'
                mainComponent={
                  <DataCard className='w-full h-[120px] flex flex-col justify-between'>
                    <div className='flex flex-col'>
                      <span className='text-darkGray text-[28px] font-semibold pt-1'>
                        {autoNotificationValue}
                      </span>
                      <span className='text-base'>
                        Taxa de autonotificação
                      </span>
                    </div>
                  </DataCard>
                }
              />
              <div />
              <CardRendrer
                loading={notificationSelector.loading}
                loaderStyle='w-full h-[120px] bg-[#EBEBEB] border border-solid border-[#E2E5EC] p-4'
                mainComponent={
                  <DataCard className='w-full h-[120px] flex items-center'>
                    <div className='flex flex-col shrink-0'>
                      <span className='text-darkGray text-[28px] font-semibold pt-1'>
                        {graveObitoStats.total ?? 0}
                      </span>
                      <div className='text-base'>
                        {/* <span className='block'>Notificações</span> */}
                        <span className='block'>Com Dano</span>
                      </div>
                    </div>
                    <div className='h-full w-px bg-borderLine shrink-0 mr-4' aria-hidden />
                    <div className='flex flex-col justify-center min-w-0'>
                      {graveObitoStats.items.map((item, index) => (
                        <div key={index} className='flex items-center gap-1 flex-nowrap'>
                          <span className='text-darkGray text-base font-bold shrink-0'>
                            {item.count ?? 0}
                          </span>
                          <span className='text-base whitespace-nowrap'>
                            {item.label}
                          </span>
                          <span className='text-xs text-gray-700 text-opacity-80 shrink-0'>
                            / {item.percent ?? 0}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </DataCard>
                }
              />
            </div>

            {/* Card de notificações múltiplas - temporariamente desativado
            <div className='col-span-5 max-w-[915px] pt-0'>
              <div className='w-[350px]'>
                <CardRendrer
                  loading={notificationSelector.loading}
                  loaderStyle='w-full h-[120px] bg-[#EBEBEB] border border-solid border-[#E2E5EC] p-4'
                  mainComponent={
                    <MassNotificationContainer
                      totalNotifications={notificationSelector.multiplierStats?.totalNotifications ?? 0}
                      totalMultiplier={notificationSelector.multiplierStats?.totalMultiplier ?? 0}
                    />
                  }
                />
              </div>
            </div>
            */ } 
            
            {/* <MultipleInfoGraph
              containerStyle={'col-span-2 h-[120px] border border-solid border-[#E2E5EC] p-0'}
              data={notificationSelector?.autoNotification}
              showMultiple={notificationSelector.multipleInfo}
            /> */}


     {/* --------------------------[SECONDARY DASHBOARDS]------------------------------ */}
    
          {dashboards.includes('notificationsPerMonthCount') && 
            <BarGraph
              text='Total de notificações por mês'
              containerStyle={'col-span-5 bg-white h-[210px] border border-solid border-[#E2E5EC] p-4'}
              option={perTotalMonthOption(notificationSelector?.notificationsPerMonthCount)}
            />
          }

          {dashboards.includes('mostReclassifiedIncidentType') && 
            <BarGraph
              text='Top 10 - Tipos de incidentes mais notificados'
              containerStyle={'col-span-5 bg-white h-[400px] border border-solid border-[#E2E5EC] p-4'}
              option={reclassifiedIncidentTypeOption(notificationSelector?.mostReclassifiedIncidentType)}
              />
          }
           
          {dashboards.includes('notificationsPerOmsCount') && 
            <BarGraph
                text='Notificações por Classificação da OMS'
                containerStyle={'col-span-5 bg-white h-[450px] border border-solid border-[#E2E5EC] p-4'}
                option={OMSOption(notificationSelector?.notificationsPerOmsCount)}
              />
          }

          {dashboards.includes('notificationsPerDamageCount') &&                
            <BirdGraph
              text='Classificação do Incidente - Pirâmide de Bird'
              containerStyle={'col-span-5 bg-white h-[450px] border border-solid border-[#E2E5EC] p-4'}
              data={notificationSelector?.notificationsPerDamageCount}
            />
          }

          {dashboards.includes('notificationsPerUnityCount') &&               
            <BarGraph
              text='Notificações por Unidade'
              containerStyle={'col-span-5 bg-white h-[450px] border  border-solid border-[#E2E5EC] p-4'}
              option={getTotalPerUnityAndSector(notificationSelector?.notificationsPerUnityCount)}
              style={{ height: 500 }}
            />
          }

          {dashboards.includes('notificationsPerIncidentSectorCount') &&  
            <BarGraph
              text='Notificações por Setor Notificado'
              containerStyle={'col-span-5 bg-white h-[450px] border  border-solid border-[#E2E5EC] p-4'}
              option={getTotalPerIncidentSector(notificationSelector?.notificationsPerIncidentSectorCount)}
              style={{ height: 500 }}
            />
          }           

          {dashboards.includes('notificationsPerSectorCount') &&  
            <BarGraph
              text='Notificações por Setor Notificante'
              containerStyle={'col-span-5 bg-white h-[450px] border  border-solid border-[#E2E5EC] p-4'}
              option={getTotalPerUnityAndSector(notificationSelector?.notificationsPerSectorCount)}
              style={{ height: 500 }}
            />
          }

          </div>
        </ReportDataNew>
      </ReportDataContainer>
    </ReportDataMainContainer>
  )
}

export { ReportData }
